const OPNFORM_CONTACT_CONFIG = {
	endpoint: '/wp-json/ec/v1/opnform/submit',
	honeypotField: 'website',
	requestTimeoutMs: 15000,
	messages: {
		success: 'Ihre Anfrage wurde gesendet. Wir melden uns in Kürze.',
		error: 'Fehler beim Senden. Bitte rufen Sie uns direkt an: +43 664 825 7441',
		validation: 'Bitte prüfen Sie die markierten Felder.',
	},
};

const REQUIRED_FIELDS = [ 'name', 'email', 'message', 'privacy' ];

const getFormFieldValue = ( formData, fieldName ) => {
	if ( fieldName === 'privacy' ) {
		return formData.get( fieldName ) ? true : '';
	}

	const value = formData.get( fieldName );
	return typeof value === 'string' ? value.trim() : value;
};

const getFieldElement = ( form, fieldName ) => form.elements.namedItem( fieldName );

const getErrorElement = ( form, fieldName ) => form.querySelector( `[data-error-for="${ fieldName }"]` );

const toggleFeedback = ( feedbackElement, type, message ) => {
	if ( ! feedbackElement ) {
		return;
	}

	feedbackElement.textContent = message || '';
	feedbackElement.classList.remove( 'd-none', 'cp-feedback--success', 'cp-feedback--error' );

	if ( ! message ) {
		feedbackElement.classList.add( 'd-none' );
		feedbackElement.removeAttribute( 'role' );
		return;
	}

	feedbackElement.classList.add( type === 'success' ? 'cp-feedback--success' : 'cp-feedback--error' );
	feedbackElement.setAttribute( 'role', type === 'success' ? 'status' : 'alert' );
};

const setFieldValidityState = ( form, fieldName, isValid ) => {
	const field = getFieldElement( form, fieldName );
	if ( ! field ) {
		return;
	}

	field.classList.toggle( 'is-invalid', ! isValid );
	field.classList.toggle( 'is-valid', isValid && field.type !== 'checkbox' );
	field.setAttribute( 'aria-invalid', isValid ? 'false' : 'true' );

	if ( field.type === 'checkbox' ) {
		const wrapper = field.closest( '.cp-form-check' );
		wrapper?.classList.toggle( 'is-invalid', ! isValid );
		wrapper?.classList.toggle( 'is-valid', isValid );
	}

	const error = getErrorElement( form, fieldName );
	if ( error ) {
		error.style.display = isValid ? 'none' : 'block';
	}
};

const clearValidationState = ( form ) => {
	form.querySelectorAll( '.is-invalid, .is-valid' ).forEach( ( element ) => {
		element.classList.remove( 'is-invalid', 'is-valid' );
		element.removeAttribute( 'aria-invalid' );
	} );

	form.querySelectorAll( '.cp-form-error' ).forEach( ( element ) => {
		element.style.display = 'none';
	} );
};

const validateForm = ( form ) => {
	let isFormValid = true;

	[ ...form.elements ].forEach( ( element ) => {
		if ( ! element.name || element.disabled || element.type === 'hidden' ) {
			return;
		}

		const isValid = element.checkValidity();
		setFieldValidityState( form, element.name, isValid );
		if ( ! isValid ) {
			isFormValid = false;
		}
	} );

	return isFormValid;
};

const buildPayload = ( formData, completionTime ) => {
	const payload = {};

	[ 'website', 'name', 'firma', 'email', 'telefon', 'bundesland', 'message', 'privacy' ].forEach( ( fieldName ) => {
		const value = getFormFieldValue( formData, fieldName );
		if ( value === '' || value === null ) {
			return;
		}

		payload[ fieldName ] = value;
	} );

	return {
		...payload,
		completion_time: completionTime,
	};
};

const resolveConfig = ( form ) => ( {
	...OPNFORM_CONTACT_CONFIG,
	endpoint: form.dataset.submitEndpoint || OPNFORM_CONTACT_CONFIG.endpoint,
} );

const setLoadingState = ( form, isLoading ) => {
	const button = form.querySelector( '#cp-submit-btn' );
	const label = button?.querySelector( '.cp-btn-label' );
	const loading = button?.querySelector( '.cp-btn-loading' );

	button?.toggleAttribute( 'disabled', isLoading );
	label?.classList.toggle( 'd-none', isLoading );
	loading?.classList.toggle( 'd-none', ! isLoading );
};

const attachRealtimeValidation = ( form ) => {
	[ ...form.elements ].forEach( ( element ) => {
		if ( ! element.name ) {
			return;
		}

		const handler = () => {
			if ( ! element.classList.contains( 'is-invalid' ) && element.type !== 'checkbox' ) {
				return;
			}

			setFieldValidityState( form, element.name, element.checkValidity() );
		};

		element.addEventListener( element.type === 'checkbox' ? 'change' : 'input', handler );
		element.addEventListener( 'blur', () => {
			setFieldValidityState( form, element.name, element.checkValidity() );
		} );
	} );
};

export const initOpnFormContactForm = () => {
	const form = document.getElementById( 'cp-kontakt-form' );
	if ( ! form ) {
		return;
	}

	const config = resolveConfig( form );
	const feedback = document.getElementById( 'cp-form-feedback' );
	let startedAt = Date.now();

	attachRealtimeValidation( form );

	form.addEventListener( 'submit', async ( event ) => {
		event.preventDefault();
		clearValidationState( form );
		toggleFeedback( feedback, 'error', '' );

		if ( ! validateForm( form ) ) {
			toggleFeedback( feedback, 'error', config.messages.validation );
			return;
		}

		const honeypotValue = getFormFieldValue( new FormData( form ), config.honeypotField );
		if ( honeypotValue ) {
			toggleFeedback( feedback, 'success', config.messages.success );
			form.reset();
			startedAt = Date.now();
			return;
		}

		const formData = new FormData( form );
		const completionTime = Math.max( 1, Math.round( ( Date.now() - startedAt ) / 1000 ) );
		const controller = new AbortController();
		const timeoutId = window.setTimeout( () => controller.abort(), config.requestTimeoutMs );

		try {
			setLoadingState( form, true );

			const response = await fetch( config.endpoint, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'application/json',
				},
				body: JSON.stringify( buildPayload( formData, completionTime ) ),
				signal: controller.signal,
			} );

			if ( ! response.ok ) {
				throw new Error( `Proxy-Request fehlgeschlagen: ${ response.status }` );
			}

			toggleFeedback( feedback, 'success', config.messages.success );
			form.reset();
			clearValidationState( form );
			startedAt = Date.now();
		} catch ( error ) {
			toggleFeedback( feedback, 'error', config.messages.error );
			console.error( error );
		} finally {
			window.clearTimeout( timeoutId );
			setLoadingState( form, false );
		}
	} );
};

export { OPNFORM_CONTACT_CONFIG };
